import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import type { PhotoUrls } from '@omokage/shared';

/**
 * API response structure for batch URL retrieval
 */
interface BatchUrlsResponse {
  success: true;
  data: Record<string, PhotoUrls>;
}

/**
 * Query key factory for photo URLs
 *
 * Performance optimization: Uses stable hash (length + first/mid/last IDs) instead of
 * full array to prevent deep comparison of large arrays (2000+ items) on every render.
 *
 * Trade-off: This approach assumes photos are always requested in contiguous ranges
 * from the visible viewport. The mid-point ID is included to prevent collisions
 * between different ID sets with the same boundaries.
 */
export const photoUrlKeys = {
  all: ['photos', 'urls'] as const,
  byIds: (ids: string[]) => {
    if (ids.length === 0) return [...photoUrlKeys.all, 0, null, null, null] as const;
    if (ids.length === 1) return [...photoUrlKeys.all, 1, ids[0], null, null] as const;

    const midIndex = Math.floor(ids.length / 2);
    return [...photoUrlKeys.all, ids.length, ids[0], ids[midIndex], ids[ids.length - 1]] as const;
  },
};

/**
 * Fetch signed URLs for multiple photos
 * Exported for use in prefetch scenarios (e.g., usePhotoMetadata)
 *
 * @param ids - Array of photo IDs to get URLs for
 * @returns Promise with URL data keyed by photo ID
 */
export async function fetchPhotoUrls(ids: string[]): Promise<Record<string, PhotoUrls>> {
  if (ids.length === 0) return {};

  // 100件ずつのチャンクに分割
  const BATCH_SIZE = 100;
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    chunks.push(ids.slice(i, i + BATCH_SIZE));
  }

  // 全チャンクを並列でリクエスト
  const results = await Promise.all(
    chunks.map((chunk) => apiClient.post<BatchUrlsResponse>('/photos/urls', { ids: chunk }))
  );

  // 結果をマージ
  return results.reduce(
    (acc, response) => ({
      ...acc,
      ...response.data,
    }),
    {} as Record<string, PhotoUrls>
  );
}

/**
 * Hook for fetching signed URLs for multiple photos at once
 *
 * @param ids - Array of photo IDs to get URLs for
 * @returns Query result with URL data keyed by photo ID
 *
 * @example
 * ```tsx
 * const { data: urls, isLoading } = usePhotoUrls(['photo-1', 'photo-2']);
 * if (urls) {
 *   console.log(urls['photo-1'].thumbnailUrl);
 * }
 * ```
 */
export function usePhotoUrls(ids: string[]) {
  return useQuery({
    queryKey: photoUrlKeys.byIds(ids),
    queryFn: () => fetchPhotoUrls(ids),
    enabled: ids.length > 0,
    // URLs are signed and have expiration, cache for 5 minutes
    staleTime: 5 * 60 * 1000,
  });
}
